import { createI18n } from 'vue-i18n';
import en from './locale/en';

// Vite turns every supported non-English locale into its own async chunk.
// Keep this explicit allowlist: the locale tree also contains work-in-progress
// directories whose index files intentionally do not build yet.
const localeLoaders = {
  ar: () => import('./locale/ar'),
  bg: () => import('./locale/bg'),
  ca: () => import('./locale/ca'),
  cs: () => import('./locale/cs'),
  da: () => import('./locale/da'),
  de: () => import('./locale/de'),
  el: () => import('./locale/el'),
  es: () => import('./locale/es'),
  et: () => import('./locale/et'),
  fa: () => import('./locale/fa'),
  fi: () => import('./locale/fi'),
  fr: () => import('./locale/fr'),
  he: () => import('./locale/he'),
  hi: () => import('./locale/hi'),
  hu: () => import('./locale/hu'),
  id: () => import('./locale/id'),
  is: () => import('./locale/is'),
  it: () => import('./locale/it'),
  ja: () => import('./locale/ja'),
  ko: () => import('./locale/ko'),
  lt: () => import('./locale/lt'),
  lv: () => import('./locale/lv'),
  ml: () => import('./locale/ml'),
  nl: () => import('./locale/nl'),
  no: () => import('./locale/no'),
  pl: () => import('./locale/pl'),
  pt: () => import('./locale/pt'),
  pt_BR: () => import('./locale/pt_BR'),
  ro: () => import('./locale/ro'),
  ru: () => import('./locale/ru'),
  sk: () => import('./locale/sk'),
  sr: () => import('./locale/sr'),
  sv: () => import('./locale/sv'),
  ta: () => import('./locale/ta'),
  th: () => import('./locale/th'),
  tr: () => import('./locale/tr'),
  uk: () => import('./locale/uk'),
  uz: () => import('./locale/uz'),
  vi: () => import('./locale/vi'),
  zh_CN: () => import('./locale/zh_CN'),
  zh_TW: () => import('./locale/zh_TW'),
};
const availableLocales = new Set(['en', ...Object.keys(localeLoaders)]);

const LOCALE_ALIASES = {
  'pt-BR': 'pt_BR',
  pt_BR: 'pt_BR',
  'zh-CN': 'zh_CN',
  'zh-Hans': 'zh_CN',
  zh_CN: 'zh_CN',
  'zh-TW': 'zh_TW',
  'zh-Hant': 'zh_TW',
  zh_TW: 'zh_TW',
};

export const resolveDashboardLocale = requestedLocale => {
  if (!requestedLocale) return 'en';

  const locale = String(requestedLocale);
  const aliased = LOCALE_ALIASES[locale] || locale.replace('-', '_');
  if (availableLocales.has(aliased)) return aliased;

  const language = aliased.split('_')[0];
  return availableLocales.has(language) ? language : 'en';
};

const notifyLocaleLoadFailure = locale => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('chatwoot:locale-load-error', { detail: { locale } })
  );
};

export const loadDashboardLocaleMessages = async (
  locale,
  loaders = localeLoaders
) => {
  if (locale === 'en') return en;

  const loader = loaders[locale];
  if (!loader) return en;
  try {
    const module = await loader();
    return module.default;
  } catch {
    notifyLocaleLoadFailure(locale);
    return null;
  }
};

const composerFor = i18n => i18n.global || i18n;
const localeRequestVersions = new WeakMap();

const currentComposerLocale = composer =>
  typeof composer.locale === 'object' && 'value' in composer.locale
    ? composer.locale.value
    : composer.locale;

export const setDashboardLocale = async (
  i18n,
  requestedLocale,
  loadMessages = loadDashboardLocaleMessages
) => {
  let locale = resolveDashboardLocale(requestedLocale);
  const composer = composerFor(i18n);
  const requestVersion = (localeRequestVersions.get(composer) || 0) + 1;
  localeRequestVersions.set(composer, requestVersion);

  if (!composer.availableLocales.includes(locale)) {
    const messages = await loadMessages(locale);
    if (localeRequestVersions.get(composer) !== requestVersion) {
      return currentComposerLocale(composer);
    }
    if (messages) composer.setLocaleMessage(locale, messages);
    else locale = 'en';
  }

  if (typeof composer.locale === 'object' && 'value' in composer.locale) {
    composer.locale.value = locale;
  } else {
    composer.locale = locale;
  }

  document.documentElement.lang = locale.replace('_', '-');
  return locale;
};

export const createDashboardI18n = async requestedLocale => {
  let locale = resolveDashboardLocale(requestedLocale);
  const messages = { en };
  if (locale !== 'en') {
    const localeMessages = await loadDashboardLocaleMessages(locale);
    if (localeMessages) messages[locale] = localeMessages;
    else locale = 'en';
  }

  return createI18n({
    legacy: false,
    locale,
    fallbackLocale: 'en',
    messages,
  });
};

// Histoire and legacy test setup consume the default export synchronously.
// Keeping that surface English-only avoids reintroducing production eagerness.
export default { en };
